import { useCallback, useRef, useState } from 'react';

/**
 * Represents a single ripple animation instance.
 */
interface Ripple<T> {
  /** Unique identifier for this ripple instance */
  id: number;
  /** The identifier of the element this ripple belongs to (e.g., button name, period value) */
  identifier: T;
}

export function useRippleEffect<T>(
  duration: number = 400,
  maxRipplesPerIdentifier: number = 3
) {
  const [ripples, setRipples] = useState<Ripple<T>[]>([]);
  const rippleIdRef = useRef(0);

  const createRipple = useCallback(
    (identifier: T) => {
      const id = rippleIdRef.current++;

      setRipples(prev => {
        // Count existing ripples for this identifier
        const identifierRipples = prev.filter(r => r.identifier === identifier);

        // If at limit, remove the oldest ripple for this identifier
        if (identifierRipples.length >= maxRipplesPerIdentifier) {
          const oldestId = identifierRipples[0].id;
          return [...prev.filter(r => r.id !== oldestId), { id, identifier }];
        }

        return [...prev, { id, identifier }];
      });

      // Auto-cleanup after duration
      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== id));
      }, duration);
    },
    [duration, maxRipplesPerIdentifier]
  );

  return { ripples, createRipple };
}
